import { useEffect, useMemo, useState } from "react";

import styles from "./insights.module.css";
import styled from "styled-components";

import InsightsArticleInfo from "./InsightsArticleInfo";
import { Trans } from "@coworker/locales";
import { FixaSkeleton } from "../../shared/wrappers/FixaSkeleton/FixaSkeleton";
import FixaChevronUpIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ChevronUpIconPath";
import FixaChevronDownIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ChevronDownIconPath";

import type {
  ArticleInsightsType,
  ItemHfbTypeInsightsType,
  RefilledAreaInsightsType,
  RefilledSalesLocationIDInsightsType,
} from "../types/insightsType";
import { InsightsKeys } from "../constants/articlesInsightsConstants";
import FixaSSRIcon from "../../shared/wrappers/FixaSSRIcon/FixaSSRIcon";
import {
  InsightsOrderByColumn,
  InsightsOrderByColumns,
  InsightsTablesType,
} from "../hooks/useInsightsData";
import InsightsTableNavigation from "./InsightsTableNavigation";

const TableContainer = styled.div`
  margin: 32px 8px;
  @media (max-width: 490px) {
    margin: 8px 0;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  text-align: center;

  th,
  td {
    font-size: 1rem;
    cursor: pointer;
    width: 25%;
  }

  td {
    padding: 8px 24px;
  }

  th {
    color: white;
    padding: 16px 24px;
    background-color: #1664c0;
  }

  th:first-child,
  td:first-child {
    border-top-left-radius: 8px;
  }

  th:last-child {
    border-top-right-radius: 8px;
  }

  td,
  th {
    text-align: right;
  }

  td:nth-child(1),
  th:nth-child(1) {
    text-align: left;
  }

  tbody {
    border: 1px solid #ddd;
    border-top: none;
  }

  tbody tr {
    transition: all 0.1s ease-in-out;
    border-bottom: 1px solid #ddd;
  }

  tbody tr:hover {
    outline: 3px solid #1664c0;
    outline-offset: -3px;
  }

  thead {
    position: sticky;
    top: -1px;
  }

  @media (max-width: 490px) {
    th,
    td {
      padding: 16px 8px;
      font-size: 0.75rem;
    }
  }
`;

type InsightsTableConfig = {
  articleInsights: {
    columns: (typeof InsightsKeys)["articleInsights"];
    data: {
      rows: ArticleInsightsType[];
      totalRowCount: number;
      pageParam: number;
    };
  };
  refilledAreaInsights: {
    columns: (typeof InsightsKeys)["refilledAreaInsights"];
    data: {
      rows: RefilledAreaInsightsType[];
      totalRowCount: number;
      pageParam: number;
    };
  };
  refilledSalesLocationIDInsights: {
    columns: (typeof InsightsKeys)["refilledSalesLocationIDInsights"];
    data: {
      rows: RefilledSalesLocationIDInsightsType[];
      totalRowCount: number;
      pageParam: number;
    };
  };
  itemHfbTypeInsights: {
    columns: (typeof InsightsKeys)["itemHfbTypeInsights"];
    data: {
      rows: ItemHfbTypeInsightsType[];
      totalRowCount: number;
      pageParam: number;
    };
  };
};

type InsightsTableProps<T extends keyof InsightsTableConfig> = {
  title: T;
  columns: InsightsTableConfig[T]["columns"];
  data: InsightsTableConfig[T]["data"];
  onRowClick: (
    row: InsightsTableConfig[T]["data"]["rows"][number],
    index: number
  ) => void;
  onColumnClick: (column: InsightsOrderByColumn) => void;
  hasSearchedArticleNo?: boolean;
  selectedColumn: InsightsOrderByColumns[T];
  isLoading: boolean;
  handlePageNavigation: (
    table: InsightsTablesType,
    direction: "previous" | "next"
  ) => void;
  resetTablePage: (table: InsightsTablesType) => void;
};

const RowSkeleton = ({ columns, table }: any) => (
  <tr>
    {columns.map((_: any, index: number) => (
      <td key={index}>
        <FixaSkeleton
          width="100%"
          height={table === "articleInsights" ? "60px" : "100%"}
        />
      </td>
    ))}
  </tr>
);

const InsightsTable = <T extends keyof InsightsTableConfig>({
  columns,
  data,
  title,
  onRowClick,
  onColumnClick,
  hasSearchedArticleNo,
  selectedColumn,
  isLoading,
  handlePageNavigation,
  resetTablePage,
}: InsightsTableProps<T>) => {
  const [hasSelectedRow, setHasSelectedRow] = useState<boolean>(false);

  const handleRowClick = (
    row: InsightsTableConfig[T]["data"]["rows"][number],
    index: number
  ) => {
    setHasSelectedRow(
      hasSearchedArticleNo && title === "articleInsights"
        ? false
        : !hasSelectedRow
    );
    onRowClick(row, index);
  };

  const isSelectedRow = useMemo(() => {
    if (title === "articleInsights" && hasSearchedArticleNo) {
      return true;
    }
    return hasSelectedRow;
  }, [hasSearchedArticleNo, hasSelectedRow, title]);

  const rowRange = useMemo(() => {
    if (!data) {
      return {
        firstRowNumber: 0,
        lastRowNumber: 0,
      };
    }
    const startRow = data.pageParam - 1;
    return {
      firstRowNumber: startRow * 5 + 1,
      lastRowNumber: startRow * 5 + data.rows.length,
    };
  }, [data]);

  useEffect(() => {
    if (!isLoading && rowRange.lastRowNumber > data?.totalRowCount) {
      resetTablePage(title as InsightsTablesType);
    }
  }, [isLoading, data, rowRange.lastRowNumber, resetTablePage, title]);

  if (!data?.rows?.length && !isLoading) {
    return null;
  }

  return (
    <TableContainer>
      <h2>
        <Trans>{title}String</Trans>
      </h2>
      <StyledTable>
        <thead>
          <tr>
            {columns.map((column, index) =>
              [
                "estimatedTurnover",
                "pieces",
                "numberOfRefillLocations",
                "numberOfArticles",
              ].includes(column) ? (
                <th
                  key={index}
                  onClick={() => onColumnClick(column as InsightsOrderByColumn)}
                >
                  <Trans>{column}String</Trans>
                  {selectedColumn?.column === column && (
                    <FixaSSRIcon
                      paths={
                        selectedColumn?.direction === "asc"
                          ? FixaChevronUpIconPath
                          : FixaChevronDownIconPath
                      }
                      color="#fff"
                    />
                  )}
                </th>
              ) : (
                <th key={index}>
                  <Trans>{column}String</Trans>
                </th>
              )
            )}
          </tr>
        </thead>
        <tbody>
          {data?.rows.map((row, index) => (
            <tr
              onClick={() => handleRowClick(row, index)}
              key={index}
              className={isSelectedRow ? styles["selected-row"] : ""}
            >
              {columns.map((column, colIndex) => {
                if (title === "articleInsights" && column === "itemNo") {
                  return (
                    <td key={colIndex}>
                      <InsightsArticleInfo
                        itemNo={(row as ArticleInsightsType).itemNo}
                      />
                    </td>
                  );
                }
                if (
                  ["refilledAreaInsights", "itemHfbTypeInsights"].includes(
                    title
                  ) &&
                  ["refillAreaId", "itemHfbNo"].includes(column)
                ) {
                  return (
                    <td key={colIndex}>
                      <Trans>{`hfb${row[column as keyof typeof row]}String`}</Trans>
                    </td>
                  );
                }
                return (
                  <td key={colIndex}>{row[column as keyof typeof row]}</td>
                );
              })}
            </tr>
          ))}
          {isLoading && (
            <>
              <RowSkeleton table={title} columns={columns} />
              <RowSkeleton table={title} columns={columns} />
              <RowSkeleton table={title} columns={columns} />
              <RowSkeleton table={title} columns={columns} />
              <RowSkeleton table={title} columns={columns} />
            </>
          )}
        </tbody>
      </StyledTable>
      <InsightsTableNavigation
        isLoading={isLoading}
        rowRange={rowRange}
        totalRowCount={data?.totalRowCount}
        handlePageNavigation={handlePageNavigation}
        table={title}
      />
    </TableContainer>
  );
};

export default InsightsTable;
